import { useEffect, useMemo } from 'react';

export const SITE_NAME = 'Pix2Text (P2T) Tools';
export const DEFAULT_SITE_URL = 'https://p2t.breezedeus.com';
export const DEFAULT_IMAGE_PATH = '/favicon.ico';

const normalizeLang = (lang = 'en') => (lang.startsWith('zh') ? 'zh' : 'en');

export const getBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return DEFAULT_SITE_URL;
};

export const getAbsoluteUrl = (path = '/') => {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${getBaseUrl()}${normalizedPath}`;
};

export const getLocalizedToolField = (tool, field, lang = 'en') => {
  const normalizedLang = normalizeLang(lang);
  const value = tool?.[field];
  if (!value) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  return value[normalizedLang] || value.en || value.zh || '';
};

export const getToolPath = (tool) => `/tools/${tool.id}`;

const getToolImage = (tool) => (
  typeof tool.icon === 'string' && tool.icon.startsWith('/')
    ? getAbsoluteUrl(tool.icon)
    : getAbsoluteUrl(DEFAULT_IMAGE_PATH)
);

const getToolKeywords = (tool, lang) => {
  const localizedTags = getLocalizedToolField(tool, 'tags', lang);
  const localizedKeywords = getLocalizedToolField(tool.seo, 'keywords', lang);
  return [...(localizedTags || []), ...(localizedKeywords || [])];
};

const cleanJson = (value) => {
  if (Array.isArray(value)) {
    return value.map(cleanJson).filter((item) => item !== undefined && item !== '');
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .map(([key, item]) => [key, cleanJson(item)])
        .filter(([, item]) => item !== undefined && item !== '' && !(Array.isArray(item) && item.length === 0))
    );
  }
  return value === null ? undefined : value;
};

const upsertMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
};

const upsertLink = (rel, href) => {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

const upsertJsonLd = (id, data) => {
  const elementId = `json-ld-${id}`;
  let element = document.getElementById(elementId);
  if (!data) {
    element?.remove();
    return;
  }
  if (!element) {
    element = document.createElement('script');
    element.id = elementId;
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(cleanJson(data));
};

export const getToolStructuredData = (tool, lang = 'en') => {
  const normalizedLang = normalizeLang(lang);
  const name = getLocalizedToolField(tool, 'name', normalizedLang);
  const description = getLocalizedToolField(tool.seo, 'geoSummary', normalizedLang)
    || getLocalizedToolField(tool, 'description', normalizedLang);
  const keywords = getToolKeywords(tool, normalizedLang);
  const useCases = getLocalizedToolField(tool.seo, 'useCases', normalizedLang);

  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name,
    alternateName: getLocalizedToolField(tool, 'name', normalizedLang === 'zh' ? 'en' : 'zh'),
    description,
    url: getAbsoluteUrl(getToolPath(tool)),
    image: getToolImage(tool),
    applicationCategory: tool.seo?.applicationCategory || 'ProductivityApplication',
    operatingSystem: tool.seo?.operatingSystem || 'Web',
    isAccessibleForFree: tool.requireAuth ? 'False' : 'True',
    inLanguage: normalizedLang === 'zh' ? ['zh-CN', 'en'] : ['en', 'zh-CN'],
    keywords: keywords.join(', '),
    featureList: useCases,
    audience: {
      '@type': 'Audience',
      audienceType: getLocalizedToolField(tool.seo, 'audience', normalizedLang),
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': getAbsoluteUrl(getToolPath(tool)),
    },
  };
};

export const getToolsCollectionStructuredData = (tools, lang = 'en') => {
  const normalizedLang = normalizeLang(lang);
  const description = normalizedLang === 'zh'
    ? 'P2T 工具市场收录免费 AI API、研究看板、Codex 指南、macOS 姿态提醒和时间线可视化等实用工具。'
    : 'The P2T tools marketplace collects free AI API, research, Codex guide, macOS posture, and timeline visualization utilities.';

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: normalizedLang === 'zh' ? 'P2T 工具市场' : 'P2T Tools Marketplace',
    description,
    url: getAbsoluteUrl('/tools'),
    inLanguage: normalizedLang === 'zh' ? 'zh-CN' : 'en',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: tools.map((tool, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: getAbsoluteUrl(getToolPath(tool)),
        name: getLocalizedToolField(tool, 'name', normalizedLang),
        description: getLocalizedToolField(tool.seo, 'geoSummary', normalizedLang)
          || getLocalizedToolField(tool, 'description', normalizedLang),
      })),
    },
  };
};

export const useSeoMetadata = ({
  title,
  description,
  path = '/',
  keywords = [],
  image = DEFAULT_IMAGE_PATH,
  lang = 'en',
  type = 'website',
  structuredData,
}) => {
  const normalizedLang = normalizeLang(lang);
  const canonicalUrl = getAbsoluteUrl(path);
  const imageUrl = getAbsoluteUrl(image);
  const keywordsContent = Array.isArray(keywords) ? keywords.join(', ') : keywords;
  const jsonLd = useMemo(
    () => (structuredData ? JSON.stringify(cleanJson(structuredData)) : ''),
    [structuredData]
  );

  useEffect(() => {
    document.documentElement.lang = normalizedLang === 'zh' ? 'zh-CN' : 'en';
    document.title = title;

    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertMeta('meta[name="keywords"]', { name: 'keywords', content: keywordsContent });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: 'index, follow, max-image-preview:large' });
    upsertMeta('meta[name="ai-summary"]', { name: 'ai-summary', content: description });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: imageUrl });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: imageUrl });
    upsertLink('canonical', canonicalUrl);
    upsertJsonLd('page', jsonLd ? JSON.parse(jsonLd) : null);
  }, [canonicalUrl, description, imageUrl, jsonLd, keywordsContent, normalizedLang, title, type]);
};
